import * as React from 'react';
import styled from 'styled-components';
import { ModalDefaultDraggable } from '../../molecules';
import {
  previewFormSetup,
  previewFormSetupUpload,
} from '../../api/formSetup';
import { convertErrorResponseToValid } from '../../utils/parseErrorResponse';
import { RASTER_DPI } from '../../utils/formSetupCoords';
import Loader from '../../atoms/Loader';

const base64ToBlobUrl = (b64: string) => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return URL.createObjectURL(
    new Blob([bytes], { type: 'application/pdf' }),
  );
};

/**
 * "Preview PDF" for the Set Placeholders editor.
 *
 * The PDF is built by the SERVER, deliberately: Form Setup's fill logic —
 * placeholder resolution, px->pt conversion, page composition — lives entirely
 * in form_setup/pdf_render.py, and reimplementing it here to draw a preview
 * would be a second implementation guaranteed to drift from the one that
 * produces real documents. A preview that agrees with itself but not with
 * production is worse than none.
 *
 * Values are each placeholder's own field name, since a form under design has
 * no permit or user to read from. Position is the question being answered.
 */
const PlaceholderPdfPreview = ({
  formId,
  dataUrl,
  formMarkup,
  fontSize,
  onClose,
}: Props) => {
  const [url, setUrl] = React.useState<string>('');
  const [error, setError] = React.useState<string>('');

  // The Font Size select is in pixels of the 200-DPI raster, not points, so a
  // reasonable-looking 12 prints at ~4pt and is easy to mistake for "the field
  // didn't render". Show what it actually comes out as.
  const printedPt = fontSize
    ? Math.round((fontSize / (RASTER_DPI / 72)) * 10) / 10
    : 0;

  React.useEffect(() => {
    let objectUrl = '';
    let cancelled = false;

    // Whichever page the CANVAS is showing is the one to preview. A data URL
    // means a PDF was just picked — either Add Form, or an edit that re-uploads
    // — and that file is not on the server yet, so it is posted. Otherwise the
    // form's saved page is already there and only its id is needed.
    const request = dataUrl
      ? previewFormSetupUpload({ dataUrl, formMarkup, fontSize })
      : previewFormSetup({ id: formId, formMarkup, fontSize });

    request
      .then(response => {
        if (cancelled) return;
        objectUrl = base64ToBlobUrl(response.data.pdf);
        setUrl(objectUrl);
      })
      .catch(err => {
        if (cancelled) return;
        const parsed = convertErrorResponseToValid(
          err?.response?.data,
        );
        const message = Object.values(parsed || {})
          .map(value =>
            (Array.isArray(value) ? value.join(' ') : `${value}`))
          .filter(Boolean)
          .join(' ');
        setError(
          message ||
            'Could not build the preview. Please try again.',
        );
      });

    return () => {
      cancelled = true;
      // Blob URLs are held by the document until revoked; without this every
      // preview leaks a whole PDF for the life of the tab.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [formId, dataUrl, formMarkup, fontSize]);

  return (
    <ModalDefaultDraggable
      title="Preview PDF"
      onClose={onClose}
      wide
      zIndex={20}
    >
      <Body>
        <Hint>
          <span>
            Sample preview — each placeholder prints its own
            field name, highlighted so it is easy to spot.
            Real documents print the actual values in these
            positions, with no highlight.
          </span>
          {!!printedPt && (
            <Small>
              Font size <b>{fontSize} px</b> prints at
              roughly <b>{printedPt} pt</b>
              {printedPt < 6
                ? ' — very small. Most forms use 24–30 px.'
                : '.'}
            </Small>
          )}
        </Hint>
        {error && <ErrorNote>{error}</ErrorNote>}
        {!error &&
          (url ? (
            <Frame title="Form preview" src={url} />
          ) : (
            <Loading>
              <Loader />
              <span>Generating preview…</span>
            </Loading>
          ))}
      </Body>
    </ModalDefaultDraggable>
  );
};

interface Props {
  /** Saved form to render against. Omitted in the Add Form flow. */
  formId?: string | number;
  /** The just-uploaded PDF as a data URL. Used when there is no formId. */
  dataUrl?: string;
  /** The editor's current markup, so un-saved moves are previewed too. */
  formMarkup?: string;
  fontSize?: number;
  onClose: () => void;
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 70vh;
  min-height: 420px;
`;

const Small = styled.span`
  display: block;
  margin-top: 6px;
  color: var(--neutral-500);
`;

const Hint = styled.div`
  flex: 0 0 auto;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-50);
  color: var(--neutral-600);
  font-size: 0.8125rem;
  line-height: 1.45;
`;

const ErrorNote = styled.div`
  flex: 0 0 auto;
  padding: 10px 12px;
  border: 1px solid var(--danger-100);
  border-radius: 8px;
  background: var(--danger-50);
  color: var(--danger-500);
  font-size: 0.8125rem;
  line-height: 1.45;
`;

const Frame = styled.iframe`
  flex: 1;
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
`;

const Loading = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--neutral-500);
  font-size: 0.875rem;
`;

export default PlaceholderPdfPreview;
